import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link2, BookmarkPlus, Loader, Check, AlertCircle, Smartphone, Monitor, ArrowLeft, Copy, Upload, FileUp, RefreshCw } from 'lucide-react';
import { recipes as recipeApi, auth, ai } from '../utils/api';

// Clean HTML tags and entities from text
const cleanHtml = (text) => {
  if (!text) return '';
  // Strip HTML tags
  let cleaned = text.replace(/<[^>]+>/g, '');
  // Decode common HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = cleaned;
  return textarea.value.trim();
};

const ImportShare = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlToImport = searchParams.get('url');
  const bookmarkLinkRef = useRef(null);
  const paprikaInputRef = useRef(null);
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  const [error, setError] = useState(null);
  const [importedRecipe, setImportedRecipe] = useState(null);
  const [copied, setCopied] = useState(false);
  const [duplicateRecipe, setDuplicateRecipe] = useState(null);
  const [reimporting, setReimporting] = useState(false);
  
  // Paprika import state
  const [paprikaImporting, setPaprikaImporting] = useState(false);
  const [paprikaResult, setPaprikaResult] = useState(null);
  const [paprikaError, setPaprikaError] = useState(null);
  
  // Check auth status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await auth.getCurrentUser();
        setIsLoggedIn(true);
      } catch {
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);
  
  // Auto-import if URL is provided and user is logged in
  useEffect(() => {
    if (urlToImport && isLoggedIn && !importing && !imported && !error) {
      handleImport();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlToImport, isLoggedIn]);
  
  const handleImport = async () => {
    if (!urlToImport) return;
    
    setImporting(true);
    setError(null);
    
    try {
      // Check for duplicate first
      try {
        const checkResponse = await recipeApi.checkRecipeExists(urlToImport);
        if (checkResponse.data.exists) {
          setDuplicateRecipe({ ...checkResponse.data, source_url: urlToImport });
          setImporting(false);
          return;
        }
      } catch (dupErr) {
        // Ignore duplicate check errors, proceed with import
      }

      // Step 1: Extract recipe from URL
      const extractResponse = await recipeApi.importFromUrl(urlToImport);
      const extractedData = extractResponse.data;
      
      console.log('Import extracted data:', {
        structured: extractedData.structured,
        title: extractedData.title,
        hasIngredients: !!extractedData.ingredients,
        ingredientCount: extractedData.ingredients?.length
      });
      
      let recipeData;
      
      // If we got structured data from the URL (JSON-LD), use it directly
      if (extractedData.structured && extractedData.title) {
        // Parse ingredients into proper format
        const parsedIngredients = (extractedData.ingredients || []).map(ing => {
          // Ingredient is a string like "1 cup flour"
          const match = ing.match(/^([\d\s\/½¼¾⅓⅔⅛]+)?\s*(cups?|tablespoons?|tbsp|teaspoons?|tsp|ounces?|oz|pounds?|lbs?|cloves?|large|medium|small|pinch|whole|sticks?|cans?)?\s*(.+)/i);
          if (match) {
            return {
              name: match[3]?.trim() || ing,
              quantity: match[1]?.trim() || '1',
              unit: match[2]?.trim() || '',
              category: null,
              preparation: null,
              aisle: 'other'
            };
          }
          return { name: ing, quantity: '', unit: '', category: null, preparation: null, aisle: 'other' };
        });
        
        // Parse instructions into proper format
        const parsedInstructions = (extractedData.instructions || []).map((step, i) => ({
          step_number: i + 1,
          text: step,
          section: null,
          duration_minutes: null
        }));
        
        // Parse times (they might be ISO durations like "PT15M")
        const parseTime = (timeVal) => {
          if (!timeVal) return null;
          // If it's already a number, return it directly
          if (typeof timeVal === 'number') return timeVal;
          // Handle string formats
          const timeStr = String(timeVal);
          const match = timeStr.match(/PT(\d+)M/);
          if (match) return parseInt(match[1]);
          const minMatch = timeStr.match(/(\d+)/);
          if (minMatch) return parseInt(minMatch[1]);
          return null;
        };
        
        recipeData = {
          title: extractedData.title,
          description: cleanHtml(extractedData.description),
          ingredients: parsedIngredients,
          instructions: parsedInstructions,
          recipe_tips: [],
          prep_time: parseTime(extractedData.prep_time),
          cook_time: parseTime(extractedData.cook_time),
          servings: parseInt(extractedData.servings) || 4,
          serving_size: null,
          cuisine_type: null,
          difficulty_level: 'medium',
          category: 'other',
          nutrition: extractedData.nutrition || null,
          source_url: extractedData.source_url || urlToImport,
          source_image: extractedData.source_image,
          generation_method: 'url_import'
        };
      } else {
        // Fallback: Use AI to structure the recipe (for pages without JSON-LD)
        const structureResponse = await ai.generateRecipe([
          { role: 'user', content: `Extract and structure this recipe as JSON:\n\n${extractedData.recipe_text}` }
        ], 'generate');
        
        // The response could be in structureResponse.data.recipe (parsed) or .message (string)
        if (structureResponse.data.recipe) {
          recipeData = structureResponse.data.recipe;
        } else if (structureResponse.data.message) {
          try {
            recipeData = JSON.parse(structureResponse.data.message);
          } catch (parseErr) {
            console.error('Failed to parse recipe message:', structureResponse.data.message);
            throw new Error('Failed to parse the recipe data from the page');
          }
        } else {
          throw new Error('No recipe data returned from AI');
        }
        
        recipeData.source_url = extractedData.source_url || urlToImport;
        recipeData.source_image = extractedData.source_image;
        recipeData.generation_method = 'url_import';
      }
      
      // Step 2: Save the recipe to the database
      const saveResponse = await recipeApi.create({
        ...recipeData,
        images: []
      });
      
      // Trigger background AI enhancement for aisles and category (don't wait for it)
      recipeApi.enhanceWithAisles(saveResponse.data.recipe_id).catch(err => {
        console.log('Background recipe enhancement failed (non-critical):', err);
      });
      
      setImportedRecipe(saveResponse.data);
      setImported(true);
    } catch (err) {
      console.error('Import failed:', err);
      setError(err.response?.data?.detail || 'Failed to import recipe. Make sure the URL is a valid recipe page.');
    } finally {
      setImporting(false);
    }
  };
  
  // Paprika import handler
  const handlePaprikaImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.paprikarecipes')) {
      setPaprikaError('Please select a valid .paprikarecipes file');
      return;
    }
    
    setPaprikaImporting(true);
    setPaprikaError(null);
    setPaprikaResult(null);
    
    try {
      const response = await recipeApi.importFromPaprika(file);
      setPaprikaResult(response.data);
    } catch (err) {
      console.error('Paprika import failed:', err);
      setPaprikaError(err.response?.data?.detail || 'Failed to import recipes from Paprika file');
    } finally {
      setPaprikaImporting(false);
      // Reset file input
      if (paprikaInputRef.current) {
        paprikaInputRef.current.value = '';
      }
    }
  };

  const handleReimport = async () => {
    if (!duplicateRecipe?.recipe_id || !duplicateRecipe?.source_url) return;
    setReimporting(true);
    setError(null);
    try {
      const extractResponse = await recipeApi.importFromUrl(duplicateRecipe.source_url);
      const extractedData = extractResponse.data;

      let recipeData;

      if (extractedData.structured && extractedData.title) {
        const parsedIngredients = (extractedData.ingredients || []).map(ing => {
          const match = ing.match(/^([\d\s\/½¼¾⅓⅔⅛]+)?\s*(cups?|tablespoons?|tbsp|teaspoons?|tsp|ounces?|oz|pounds?|lbs?|cloves?|large|medium|small|pinch|whole|sticks?|cans?)?\s*(.+)/i);
          if (match) {
            return { name: match[3]?.trim() || ing, quantity: match[1]?.trim() || '1', unit: match[2]?.trim() || '', category: null, preparation: null, aisle: 'other' };
          }
          return { name: ing, quantity: '', unit: '', category: null, preparation: null, aisle: 'other' };
        });
        const parsedInstructions = (extractedData.instructions || []).map((step, i) => ({
          step_number: i + 1, text: step, section: null, duration_minutes: null
        }));
        const parseTime = (timeVal) => {
          if (!timeVal) return null;
          if (typeof timeVal === 'number') return timeVal;
          const timeStr = String(timeVal);
          const match = timeStr.match(/PT(\d+)M/);
          if (match) return parseInt(match[1]);
          const minMatch = timeStr.match(/(\d+)/);
          if (minMatch) return parseInt(minMatch[1]);
          return null;
        };
        recipeData = {
          ingredients: parsedIngredients,
          instructions: parsedInstructions,
          description: cleanHtml(extractedData.description),
          prep_time: parseTime(extractedData.prep_time),
          cook_time: parseTime(extractedData.cook_time),
          servings: parseInt(extractedData.servings) || 4,
          nutrition: extractedData.nutrition || null,
          source_image: extractedData.source_image,
        };
      } else {
        const structureResponse = await ai.generateRecipe([
          { role: 'user', content: `Extract and structure this recipe as JSON:\n\n${extractedData.recipe_text}` }
        ], 'generate');
        let parsed;
        if (structureResponse.data.recipe) {
          parsed = structureResponse.data.recipe;
        } else if (structureResponse.data.message) {
          parsed = JSON.parse(structureResponse.data.message);
        } else {
          throw new Error('No recipe data returned from AI');
        }
        recipeData = {
          ingredients: parsed.ingredients || [],
          instructions: parsed.instructions || [],
          description: parsed.description || '',
          prep_time: parsed.prep_time,
          cook_time: parsed.cook_time,
          servings: parsed.servings,
          nutrition: parsed.nutrition,
          source_image: extractedData.source_image,
        };
      }

      await recipeApi.update(duplicateRecipe.recipe_id, recipeData);
      recipeApi.enhanceWithAisles(duplicateRecipe.recipe_id).catch(() => {});

      setDuplicateRecipe(null);
      setImportedRecipe({ recipe_id: duplicateRecipe.recipe_id, title: duplicateRecipe.title });
      setImported(true);
    } catch (err) {
      console.error('Re-import failed:', err);
      setError(err.response?.data?.detail || 'Failed to re-import recipe');
    } finally {
      setReimporting(false);
    }
  };
  
  const baseUrl = window.location.origin;
  // Bookmarklet code - navigates current tab to avoid popup blockers
  const bookmarkletCode = `javascript:location.href='${baseUrl}/import?url='+encodeURIComponent(location.href);`;
  
  // Set the href directly on the DOM element to bypass React's javascript: URL blocking
  useEffect(() => {
    if (bookmarkLinkRef.current) {
      bookmarkLinkRef.current.setAttribute('href', bookmarkletCode);
    }
  }, [bookmarkletCode]);
  
  const handleCopyBookmarklet = () => {
    navigator.clipboard.writeText(bookmarkletCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // If no URL provided, show setup instructions
  if (!urlToImport) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookmarkPlus size={32} className="text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Share to Honey</h1>
            <p className="text-gray-600">Save recipes from any website with one tap</p>
          </div>
          
          {/* Setup Instructions */}
          <div className="space-y-6">
            {/* Desktop Instructions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <Monitor size={24} className="text-blue-500" />
                <h2 className="text-lg font-semibold text-slate-800">Desktop Browser</h2>
              </div>
              <ol className="space-y-4 text-gray-600">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">1</span>
                  <span>Drag this button to your bookmarks bar:</span>
                </li>
                <li className="pl-9">
                  <a
                    ref={bookmarkLinkRef}
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    draggable="true"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors cursor-grab active:cursor-grabbing"
                  >
                    <span>🐝</span>
                    Save to Honey
                  </a>
                  <p className="text-sm text-gray-500 mt-2">
                    (Drag this to your bookmarks bar)
                  </p>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">2</span>
                  <span>When you find a recipe online, click the bookmark!</span>
                </li>
                <li className="flex gap-3 pt-2 border-t border-gray-100 mt-2">
                  <span className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">?</span>
                  <div>
                    <span className="text-sm">Can't drag? Copy the code instead:</span>
                    <button
                      onClick={handleCopyBookmarklet}
                      className="ml-2 inline-flex items-center gap-1 text-sm text-amber-600 font-medium hover:underline"
                    >
                      <Copy size={14} />
                      {copied ? 'Copied!' : 'Copy code'}
                    </button>
                    <p className="text-xs text-gray-400 mt-1">
                      Then create a new bookmark and paste as the URL
                    </p>
                  </div>
                </li>
              </ol>
            </div>
            
            {/* Mobile Instructions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <Smartphone size={24} className="text-green-500" />
                <h2 className="text-lg font-semibold text-slate-800">iPhone / Android</h2>
              </div>
              <ol className="space-y-4 text-gray-600">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">1</span>
                  <div>
                    <span><strong>First, bookmark this page</strong> (tap Share → Add Bookmark)</span>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">2</span>
                  <div>
                    <span>Copy the code below:</span>
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <code className="text-xs text-gray-600 break-all">{bookmarkletCode}</code>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(bookmarkletCode);
                        alert('Copied! Now edit your bookmark and replace the URL with this code.');
                      }}
                      className="mt-2 text-sm text-amber-600 font-medium"
                    >
                      Tap to Copy
                    </button>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">3</span>
                  <div>
                    <span><strong>Tap and hold to edit the bookmark you just created:</strong></span>
                    <ul className="mt-1 text-sm text-gray-500 list-disc pl-4 space-y-1">
                      <li>Open Bookmarks → tap and hold the bookmark</li>
                      <li>Select "Edit" (or "Edit Bookmark")</li>
                      <li>Delete the URL and paste the copied code</li>
                      <li>Rename it "Save to Honey 🐝"</li>
                    </ul>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">4</span>
                  <span>When viewing a recipe online, open bookmarks and tap "Save to Honey"!</span>
                </li>
              </ol>
            </div>
            
            {/* Paprika Import Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <FileUp size={24} className="text-orange-500" />
                <h2 className="text-lg font-semibold text-slate-800">Import from Paprika</h2>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Switching from Paprika? Import all your recipes in one go!
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">How to export from Paprika:</p>
                <ol className="text-sm text-gray-600 space-y-1.5 list-decimal pl-4">
                  <li>Open Paprika app → <strong>Settings</strong> (in sidebar)</li>
                  <li>Tap <strong>Export Recipes</strong></li>
                  <li>Select <strong>"Paprika Recipe Format"</strong></li>
                  <li>Turn <strong>OFF</strong> "Use Unicode filenames"</li>
                  <li>Tap <strong>Export</strong> (saves to Files/iCloud Drive on your phone)</li>
                  <li>Tap the below button to access the file and import to Honey</li>
                </ol>
              </div>
              
              {/* File Input */}
              <input
                ref={paprikaInputRef}
                type="file"
                accept=".paprikarecipes"
                onChange={handlePaprikaImport}
                className="hidden"
                id="paprika-file-input"
              />
              
              {!paprikaResult && (
                <button
                  onClick={() => paprikaInputRef.current?.click()}
                  disabled={paprikaImporting || !isLoggedIn}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white rounded-lg font-medium transition-colors"
                >
                  {paprikaImporting ? (
                    <>
                      <Loader size={20} className="animate-spin" />
                      Importing recipes...
                    </>
                  ) : (
                    <>
                      <Upload size={20} />
                      Select .paprikarecipes file
                    </>
                  )}
                </button>
              )}
              
              {!isLoggedIn && (
                <p className="text-sm text-amber-600 mt-2 text-center">
                  Please sign in first to import recipes
                </p>
              )}
              
              {/* Paprika Import Results */}
              {paprikaResult && (
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
                    <Check size={20} />
                    Imported {paprikaResult.imported_count} recipe{paprikaResult.imported_count !== 1 ? 's' : ''}!
                  </div>
                  {paprikaResult.imported_recipes?.length > 0 && (
                    <ul className="text-sm text-green-600 space-y-1 mb-3">
                      {paprikaResult.imported_recipes.slice(0, 5).map((r, i) => (
                        <li key={i}>• {r.title}</li>
                      ))}
                      {paprikaResult.imported_recipes.length > 5 && (
                        <li className="text-gray-500">...and {paprikaResult.imported_recipes.length - 5} more</li>
                      )}
                    </ul>
                  )}
                  {paprikaResult.error_count > 0 && (
                    <p className="text-sm text-amber-600">
                      {paprikaResult.error_count} recipe{paprikaResult.error_count !== 1 ? 's' : ''} skipped (already in your collection)
                    </p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => navigate('/recipes')}
                      className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      View Recipe Box
                    </button>
                    <button
                      onClick={() => {
                        setPaprikaResult(null);
                        setPaprikaError(null);
                      }}
                      className="px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                    >
                      Import More
                    </button>
                  </div>
                </div>
              )}
              
              {/* Paprika Error */}
              {paprikaError && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle size={20} />
                    <span className="text-sm">{paprikaError}</span>
                  </div>
                  <button
                    onClick={() => setPaprikaError(null)}
                    className="mt-2 text-sm text-red-600 hover:underline"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
            
            {/* Alternative: Manual URL */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="font-medium text-slate-800 mb-2">Or paste a URL directly</h3>
              <p className="text-sm text-gray-600 mb-4">
                You can also import recipes from the main app. Go to{' '}
                <button onClick={() => navigate('/browse')} className="text-amber-600 font-medium">
                  Browse Websites
                </button>
                {' '}and paste any recipe URL.
              </p>
            </div>
          </div>
          
          {/* Back to App */}
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/recipes')}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={18} />
              Back to Recipe Box
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // URL provided - show import status
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md w-full text-center">
        {/* Not logged in - redirect to main login */}
        {!isLoggedIn && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={32} className="text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Sign In Required</h2>
            <p className="text-gray-600 mb-6">Please sign in to save this recipe to your Recipe Box.</p>
            <p className="text-sm text-gray-500 mb-4 p-3 bg-gray-50 rounded-lg break-all">
              {urlToImport}
            </p>
            <button
              onClick={() => {
                // Store return URL and go to main login page
                const returnUrl = `/import?url=${encodeURIComponent(urlToImport)}`;
                sessionStorage.setItem('intendedUrl', returnUrl);
                navigate('/login');
              }}
              className="w-full py-3 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition-colors"
            >
              Sign In with Google
            </button>
          </>
        )}
        
        {/* Importing */}
        {isLoggedIn && importing && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader size={32} className="text-amber-600 animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Importing Recipe...</h2>
            <p className="text-gray-600 mb-4">Extracting recipe from:</p>
            <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg break-all">
              {urlToImport}
            </p>
          </>
        )}
        
        {/* Duplicate Found */}
        {isLoggedIn && duplicateRecipe && !importing && !imported && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={32} className="text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">You already have this recipe!</h2>
            {duplicateRecipe.title && (
              <p className="text-gray-600 mb-6">{duplicateRecipe.title}</p>
            )}
            <div className="space-y-3">
              <button
                onClick={() => navigate(`/recipe/${duplicateRecipe.recipe_id}`)}
                className="w-full py-3 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition-colors"
                data-testid="view-existing-recipe-btn"
              >
                View Existing Recipe
              </button>
              <button
                onClick={handleReimport}
                disabled={reimporting}
                className="w-full py-3 bg-white text-amber-700 border border-amber-300 rounded-xl font-medium hover:bg-amber-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="reimport-recipe-btn"
              >
                <RefreshCw size={16} className={reimporting ? 'animate-spin' : ''} />
                {reimporting ? 'Re-importing...' : 'Re-import'}
              </button>
            </div>
          </>
        )}
        
        {/* Success */}
        {isLoggedIn && imported && importedRecipe && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Recipe Saved!</h2>
            <p className="text-gray-600 mb-6">{importedRecipe.title}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate(`/recipe/${importedRecipe.recipe_id}`)}
                className="w-full py-3 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition-colors"
              >
                View Recipe
              </button>
              <button
                onClick={() => {
                  window.close();
                  // Fallback if window.close() doesn't work (non-JS-opened windows)
                  setTimeout(() => navigate('/recipe-box'), 100);
                }}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Close Window
              </button>
            </div>
          </>
        )}
        
        {/* Error */}
        {isLoggedIn && error && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={32} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Import Failed</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500 mb-6 p-3 bg-gray-50 rounded-lg break-all">
              {urlToImport}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleImport}
                className="w-full py-3 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/', { state: { openChat: true, prefillMessage: `I'd like to add this recipe: ${urlToImport}` } })}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Add via Chat
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportShare;
